import { CIActionBtnGroup, CIStatusCapsule } from 'ccs-ui-components';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { SORT_STRINGS, URL_REGISTRY } from '../../../../constants';
import { IActiveSource, ISourceData } from '../../../../store/actions/types/sources/GetAllSourceRegistries';
import AssociationList from '../association-list/AssociationList';
import classes from './SourceAssociationList.module.scss';

interface ISourceAssociationList {
  sourceData: IActiveSource
  queryParams: { [key: string]: string };
  setQueryParams: Dispatch<SetStateAction<{ [key: string]: string; }>>
  isLoading: boolean,
  errorMessage: string
}

const CIGridLabels = {
  SOURCE_NAME: 'sourceName',
  SOURCE_STATUS: 'status',
};

const SourceAssociationList = ({ sourceData, setQueryParams, queryParams, isLoading, errorMessage }: ISourceAssociationList) => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const [selectedSortField, setSelectedSortField] = useState(queryParams.sort);

  const [, setSelectedSortFieldOrder] = useState(true);
  const sortedObj = { sortedName: queryParams.sort, sortedOrder: queryParams.type };

  /**
  * @method onGetPageHandler
  * @description Handles the source when a page number is selected.
  * @param {number} pageNumber - The selected page number.
  */
  const onGetPageHandler = (pageNumber: number) => {
    setQueryParams((pre: { [key: string]: string }) => ({
      ...pre,
      page: String(pageNumber),
    }));
  };

  /**
  * @method onGetItemsHandler
  * @description Handles the source when an item is selected.
  * @param itemNumber - The number of the selected item.
  */
  const onGetItemsHandler = (itemNumber: number) => {
    setQueryParams((pre) => ({
      ...pre,
      item: String(itemNumber),
    }));
  };

  const footerOptionsObj = {
    itemsPerPage: Number(queryParams.item),
    currentPageNumber: Number(queryParams.page),
    numberOfPages: sourceData?.numberOfPages,
    onCurrentPageNumberChange: onGetPageHandler,
    onItemsPerPageChange: onGetItemsHandler
  };

  const footerObj = { footerType: 'paging', footerOptions: footerOptionsObj };

  /**
  * @method sortHandler
  * @description Handles the sorting of the event list based on the selected label and state.
  * @param label - The label to sort by.
  * @param selectedLabelState - The state of the selected label (true for ascending, false for descending).
  */
  const sortHandler = (label: string, selectedLabelState: boolean) => {
    setQueryParams((pre) => ({
      ...pre,
      sort: label,
      type: selectedLabelState ? SORT_STRINGS.ASC : SORT_STRINGS.DESC
    }));
  };
  /**
  * @method handleSortClick
  * @description Handles the click source for sorting the source list.
  */
  const handleSortClick = (e: React.MouseEvent<HTMLElement>, label: string) => {
    if (selectedSortField === label) {
      setSelectedSortFieldOrder((preValue) => {
        sortHandler(selectedSortField, !preValue);
        return !preValue;
      });
    } else {
      setSelectedSortField(label);
      setSelectedSortFieldOrder(true);
      sortHandler(label, true);
    }
  };

  /**
  * To render source name in tooltip
  * @param row
  * @returns JSX element
  */
  const renderSourceName = (row: ISourceData) => (
    <div className={classes['source-name-wrapper']}>
      <span className={classes['source-name']} title={row.sourceName}>
        {row.sourceName}
      </span>
      <p className={classes['source-version']}>
        {translate('event_list_component.txt_version', {
          replace: {
            version: row.version
          }
        })}
      </p>

    </div>
  );

  /**
  * method to populate a single source status data cell jsx with data
  * @param row source data
  * @returns source status column data cell jsx element
  */
  const renderSourceStatus = (row: ISourceData) => (
    <div>
      <CIStatusCapsule status={row.status} />
    </div>
  );

  /**
  * @method navigateToSourceRegistryView
  * @description To navigate to view source screen
  * @param name
  */
  const navigateToSourceRegistryView = (name: string, version: number) => {
    navigate(`${URL_REGISTRY.VIEW_SOURCE_REGISTRY}/${name}/${version}`);
  };

  /**
  * @method renderColAction
  * @description To render column action buttons
  * @param row
  * @returns JSX element
  */
  const renderColAction = (row: ISourceData) => (
    <CIActionBtnGroup buttons={[{
      label: translate('info_message.lbl_source_navigation'),
      key: 'viewIcon',
      onClick: () => {
        navigateToSourceRegistryView(row.sourceName, row.version);
      },
      testId: 'source-nav-button'
    }]}
    />
  );

  const headers = [
    {
      label: translate('sourceList.sourceName'),
      name: CIGridLabels.SOURCE_NAME,
      sortOptions: {
        callback: handleSortClick
      },
      options: { breakPointWidth: 6 },
      columnData: { field: 'sourceName', render: renderSourceName }
    },
    {
      name: CIGridLabels.SOURCE_STATUS,
      label: translate('sourceList.status'),
      sortOptions: {
        callback: handleSortClick
      },
      options: { breakPointWidth: 5 },
      columnData: {
        field: 'status', render: renderSourceStatus
      }
    },
    {
      label: '',
      name: 'action',
      options: { breakPointWidth: 1 },
      columnData: { field: 'action', render: renderColAction, className: 'render-action' }
    }
  ];

  return (
    <div>
      <AssociationList headers={headers} tableData={sourceData?.sources} sorted={sortedObj} footer={footerObj} errorMessage={errorMessage} isLoading={isLoading} />
    </div>
  );
};
export default SourceAssociationList;
